import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { enUS, es, fr, de, ptBR, type Locale } from 'date-fns/locale';

import { SupportedLanguage, SUPPORTED_LANGUAGES, LanguageOption } from './types';
import { changeLanguage as changeI18nLanguage } from './config';

/**
 * Mapping of language codes to date-fns locales
 */
const DATE_LOCALES: Record<SupportedLanguage, Locale> = {
  en: enUS,
  es: es,
  fr: fr,
  de: de,
  pt: ptBR,
};

/**
 * Hook for managing language state and switching
 */
export function useLanguage() {
  const { i18n } = useTranslation();

  const currentLanguage = (i18n.language as SupportedLanguage) || 'en';

  const currentLanguageOption = SUPPORTED_LANGUAGES.find(
    (lang) => lang.code === currentLanguage
  ) || SUPPORTED_LANGUAGES[0];

  const dateLocale = useMemo(() => {
    return DATE_LOCALES[currentLanguage] || enUS;
  }, [currentLanguage]);

  const setLanguage = useCallback(
    async (lang: SupportedLanguage) => {
      await changeI18nLanguage(lang);
    },
    []
  );

  return {
    /** Current language code (e.g., 'en', 'es') */
    currentLanguage,
    /** Current language option with full details */
    currentLanguageOption,
    /** All available languages */
    languages: SUPPORTED_LANGUAGES,
    /** Change the current language */
    setLanguage,
    /** Check if a language is currently selected */
    isCurrentLanguage: (lang: SupportedLanguage) => currentLanguage === lang,
    /** date-fns locale for formatting dates */
    dateLocale,
  };
}

export type { LanguageOption, SupportedLanguage };
